'use client';
import { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { contacts } from '@/_config';

/* ─── Per-topic FAQ data ────────────────────────────────────────────────────── */
export const getTopicData = (n = 7) => ({
    orders: {
        id:          'orders',
        icon:        'pi pi-box',
        title:       'Orders & Delivery',
        subtitle:    'Track orders, delivery timelines, shipping queries',
        color:       '#6366f1',
        bg:          '#eef2ff',
        gradient:    'linear-gradient(135deg, #6366f1, #8b5cf6)',
        faqs: [
            {
                q: 'How do I track my order?',
                a: 'Once your order is shipped, you will receive an email and SMS with the AWB/tracking number. You can also log in to your account, navigate to My Orders, and click "Track Shipment" on any active order for real-time updates.',
            },
            {
                q: 'How long does delivery take?',
                a: 'Standard delivery takes 3–7 business days depending on your location. Metro cities typically receive orders in 3–4 days, while remote areas may take up to 7 days. Express delivery (where available) is 1–2 business days.',
            },
            {
                q: 'Can I change my delivery address after placing an order?',
                a: 'Address changes are possible only if the order is still in "Placed" or "Confirmed" status. Please contact our support team immediately via WhatsApp or phone. Once the order is shipped, address changes are not possible.',
            },
            {
                q: 'My order hasn\'t arrived by the expected date. What should I do?',
                a: 'Check the tracking link in your shipping confirmation email first. If the courier shows an issue or it\'s been more than 2 days past the expected date, contact our support team with your Order ID — we\'ll investigate and resolve it promptly.',
            },
            {
                q: 'Do you deliver pan India?',
                a: 'Yes, we deliver to all serviceable pin codes across India. You can check delivery availability for your pin code on the product page or the cart page using our "Check Delivery" feature.',
            },
            {
                q: 'What are the shipping charges?',
                a: 'Shipping is FREE on all orders. For select remote areas or heavy items, a nominal delivery fee may apply — this will be clearly shown at checkout before you confirm your order.',
            },
            {
                q: 'Can I schedule a specific delivery time or date?',
                a: 'Scheduled delivery is currently not available. Our courier partners deliver between 9 AM – 8 PM on business days. If you\'re unavailable, the courier will attempt re-delivery the next day or leave a note for you to collect from the hub.',
            },
            {
                q: 'What happens if I miss my delivery attempt?',
                a: 'The courier will attempt delivery up to 3 times. After 3 failed attempts, the package is returned to our warehouse. You can contact us to arrange re-dispatch — additional shipping charges may apply for re-dispatch.',
            },
            {
                q: 'Can I change or cancel my order after placing it?',
                a: 'You can cancel or modify orders in "Placed" or "Confirmed" status directly from your My Orders page. Once the order is in "Processing" or "Shipped" status, cancellation is no longer possible — you\'d need to initiate a return after delivery.',
            },
        ],
    },

    returns: {
        id:          'returns',
        icon:        'pi pi-refresh',
        title:       'Returns & Refunds',
        subtitle:    'Return policy, refund timelines, exchange process',
        color:       '#f97316',
        bg:          '#fff7ed',
        gradient:    'linear-gradient(135deg, #f97316, #ef4444)',
        faqs: [
            {
                q: 'What is the return policy?',
                a: `We accept returns within ${n} days of delivery for products that are defective, damaged in transit, or significantly different from the description. The product must be unused, in original packaging, with all accessories and invoice included.`,
            },
            {
                q: 'How do I initiate a return?',
                a: 'Contact our support team via WhatsApp or email with your Order ID and the reason for return. We\'ll send you a return pickup confirmation within 24 hours. Please ensure the item is packed securely in its original box.',
            },
            {
                q: 'When will I receive my refund?',
                a: 'Once we receive and inspect the returned product (typically 2–3 days after pickup), the refund is processed within 5–7 business days to your original payment method. UPI/Net Banking refunds are usually faster than card refunds.',
            },
            {
                q: 'Can I exchange a product instead of a refund?',
                a: `Yes! If the product is defective or you received the wrong item, we offer a free exchange. Please contact support within ${n} days of delivery with photos of the issue. Exchanges for change-of-mind are subject to availability.`,
            },
            {
                q: 'What items are not eligible for return?',
                a: `Products that have been installed or show signs of use, items with tampered or missing serial numbers, products damaged due to mishandling by the customer, and orders placed beyond the ${n}-day return window are not eligible for returns.`,
            },
            {
                q: 'My refund hasn\'t been credited. What should I do?',
                a: 'If it\'s been more than 7 business days since your return was confirmed, please contact us with your Order ID and the last 4 digits of the account/card. We\'ll raise an escalation with the payment gateway and resolve it within 2 business days.',
            },
            {
                q: 'Will the return shipping be free?',
                a: 'Yes — we arrange free pickup for all valid return requests. You do not need to drop the package at any courier office. Simply have it ready packed and our courier partner will collect it from your doorstep.',
            },
        ],
    },

    warranty: {
        id:          'warranty',
        icon:        'pi pi-shield',
        title:       'Warranty',
        subtitle:    'Coverage, validity, repair process & claims',
        color:       '#10b981',
        bg:          '#f0fdf4',
        gradient:    'linear-gradient(135deg, #10b981, #1bb8a7)',
        faqs: [
            {
                q: 'What is the warranty period?',
                a: 'Ennix products carry a standard 1-year manufacturer warranty from the date of purchase against manufacturing defects. Some premium product lines may carry extended warranty — check the product page for specific details.',
            },
            {
                q: 'How do I register my product for warranty?',
                a: 'Warranty registration is automatic if purchased from our official website or authorized platforms (Amazon/Flipkart). Keep your invoice/order confirmation as proof of purchase — you\'ll need it for warranty claims.',
            },
            {
                q: 'How do I raise a warranty claim?',
                a: 'Email us at info@ennixelectronics.com or WhatsApp us at +91-9211843332 with: (1) Order ID or invoice, (2) Product model number, (3) Description and photos/video of the defect. Our team will respond within 24 hours with next steps.',
            },
            {
                q: 'What is covered under warranty?',
                a: 'The warranty covers: manufacturing defects, PCB/electronic failures under normal use, and internal component failures. It does NOT cover physical damage, water damage, lightning damage, voltage surge damage, or damage from improper installation.',
            },
            {
                q: 'What voids the warranty?',
                a: 'Warranty is void if the product is physically damaged, tampered or opened without authorization, used with incompatible voltage/wiring, or if the serial number sticker is removed or defaced.',
            },
            {
                q: 'How long does warranty repair/replacement take?',
                a: 'After receiving the product at our service center, diagnosis takes 2–3 days. Repair or replacement is completed within 7–10 business days. We\'ll ship the repaired/replacement unit back to you at no cost.',
            },
            {
                q: 'What if my product fails just after the warranty expires?',
                a: 'We offer Out-of-Warranty (OOW) repair service at a nominal charge. Contact our support team — we\'ll assess the issue and provide a cost estimate before any repairs are carried out.',
            },
        ],
    },

    installation: {
        id:          'installation',
        icon:        'pi pi-wrench',
        title:       'Installation Help',
        subtitle:    'Setup guides, wiring help, professional support',
        color:       '#f59e0b',
        bg:          '#fffbeb',
        gradient:    'linear-gradient(135deg, #f59e0b, #f97316)',
        faqs: [
            {
                q: 'Can I install Ennix smart switches myself?',
                a: 'Yes! Ennix smart switches are designed for straightforward DIY installation. Each product ships with a detailed instruction manual. However, since installation involves mains electricity (220V), we strongly recommend having a certified electrician do it for safety.',
            },
            {
                q: 'Do you provide professional installation support?',
                a: 'We offer video-call installation assistance — our technician will guide you or your electrician through the process live. Contact us on WhatsApp to schedule a session. On-site installation service is available in select cities (Delhi NCR) at an additional charge.',
            },
            {
                q: 'My device is not turning on after installation. What should I do?',
                a: 'Check the following: (1) Ensure the live and neutral wires are correctly connected (refer to the wiring diagram in the manual). (2) Confirm the MCB/fuse for that circuit is switched on. (3) Test with a voltage tester to confirm power is reaching the switch terminals. If still not working, contact support immediately — do not attempt to open the device.',
            },
            {
                q: 'Can Ennix smart switches be installed in older homes without a neutral wire?',
                a: 'Most Ennix smart switches require a neutral wire for proper operation. If your existing wiring is a 2-wire system (no neutral), please check the specific product page — some models support no-neutral operation. Consult an electrician before purchase if unsure.',
            },
            {
                q: 'Can I use Ennix switches with any load type (LED, fan, bulb)?',
                a: 'Yes — Ennix switches are compatible with LED lights, CFL lights, incandescent bulbs, and fans. For fan speed control, use our dedicated fan speed controllers. Check the product\'s maximum load rating (typically 2.5A or 10A) before connecting high-power appliances.',
            },
            {
                q: 'Where can I find the installation manual?',
                a: 'The installation guide is included in the product box. You can also download the PDF manual from the product page on our website, or request it from our support team via WhatsApp/email.',
            },
        ],
    },

    app: {
        id:          'app',
        icon:        'pi pi-mobile',
        title:       'App & Connectivity',
        subtitle:    'Smart Life app, Wi-Fi pairing, Alexa, Google, Siri',
        color:       '#0ea5e9',
        bg:          '#f0f9ff',
        gradient:    'linear-gradient(135deg, #0ea5e9, #6366f1)',
        faqs: [
            {
                q: 'My device is not connecting to the Smart Life app. What should I do?',
                a: 'Ensure your phone is connected to a 2.4 GHz Wi-Fi network (Ennix devices do NOT support 5 GHz). Reset the device by holding the button for 5 seconds until it blinks rapidly. Then open Smart Life → "+" → Add Device → follow the on-screen steps. Make sure Bluetooth is enabled on your phone during pairing.',
            },
            {
                q: 'My device shows "offline" in the app but is physically powered on.',
                a: 'This typically means the device lost its Wi-Fi connection. Check your router — if it\'s working, try: (1) Turn the physical switch off and on. (2) Restart your router. (3) If still offline, remove the device from the app and re-add it. If your Wi-Fi password changed recently, the device will need to be re-paired.',
            },
            {
                q: 'How do I connect to Amazon Alexa?',
                a: 'Open the Alexa app → Menu → Skills & Games → search "Smart Life" → Enable skill → Sign in with your Smart Life account. Your devices will auto-discover. Say "Alexa, discover devices" if they don\'t appear. Then you can control them with voice commands like "Alexa, turn on living room light."',
            },
            {
                q: 'How do I connect to Google Home?',
                a: 'Open the Google Home app → "+" → Set up device → Works with Google → search "Smart Life" → Sign in. Your devices will appear in Google Home. You can create rooms and routines for automation.',
            },
            {
                q: 'How do I use Ennix devices with Apple HomeKit / Siri?',
                a: 'Matter-enabled Ennix devices support Apple HomeKit natively. Add them to the Home app by scanning the Matter QR code on the device. For non-Matter devices, use the "Smart Life" skill or a HomeBridge setup. Check the product page to confirm Matter support for your specific device.',
            },
            {
                q: 'How do I add multiple family members to control the devices?',
                a: 'In the Smart Life app, go to Me → Home Management → select your home → Add Member → enter the family member\'s Smart Life account. They\'ll receive an invite and can control all shared devices once accepted.',
            },
            {
                q: 'How do I factory reset my Ennix device?',
                a: 'Hold the button on the device for 10 seconds until the LED blinks rapidly (3 times), indicating a successful factory reset. The device will be removed from your Smart Life account automatically. You\'ll need to re-pair it to use it again.',
            },
            {
                q: 'Can I use Ennix devices without a smartphone?',
                a: 'Yes — all Ennix smart switches retain their physical touch/button functionality. Even without the app or internet, you can operate them manually. Smart features (voice control, scheduling, remote access) require the app and internet.',
            },
        ],
    },

    products: {
        id:          'products',
        icon:        'pi pi-shopping-cart',
        title:       'Product Information',
        subtitle:    'Specs, compatibility, matter, comparisons',
        color:       '#8b5cf6',
        bg:          '#f5f3ff',
        gradient:    'linear-gradient(135deg, #8b5cf6, #6366f1)',
        faqs: [
            {
                q: 'What smart home ecosystems do Ennix devices support?',
                a: 'Ennix devices support Amazon Alexa, Google Home, and Apple Siri (via SmartLife or Matter). Matter-enabled models offer universal compatibility across all major smart home platforms without needing separate bridges or hubs.',
            },
            {
                q: 'What is the difference between Wi-Fi and Zigbee devices?',
                a: 'Wi-Fi devices connect directly to your router — easy setup, no hub needed, but each device uses a Wi-Fi slot. Zigbee devices form a mesh network with low power usage and strong range, but require a Zigbee hub/gateway. Zigbee is better for large homes with many devices.',
            },
            {
                q: 'What is a Matter device?',
                a: 'Matter is a universal smart home standard that allows devices to work seamlessly with Amazon Alexa, Google Home, Apple HomeKit, and SmartThings without needing separate apps or bridges. Ennix Matter devices offer maximum interoperability and local control (no cloud required for basic operations).',
            },
            {
                q: 'Do Ennix smart switches work without internet?',
                a: 'Yes — the physical touch/button function works without internet at all times. However, remote control via app, voice assistant commands, and scheduled automations require an active internet connection.',
            },
            {
                q: 'Are Ennix devices energy-efficient?',
                a: 'Yes. Ennix smart switches have an extremely low standby power consumption (typically < 0.5W). Additionally, features like scheduling and energy monitoring (on select models) help you reduce overall electricity usage.',
            },
            {
                q: 'Are Ennix products BIS / ISI certified?',
                a: 'Ennix products meet applicable Indian electrical safety standards. Certification details are listed on the product page. All products undergo quality testing before dispatch.',
            },
            {
                q: 'Can I use Ennix smart switches with high-wattage appliances like geysers or ACs?',
                a: 'Most standard Ennix switches are rated for lighting loads (up to 600W). For high-power appliances (geysers, ACs, motors), please check the specific product\'s load rating. We offer dedicated high-load smart plugs and switches for heavy appliances.',
            },
            {
                q: 'How do I know which product is right for my home?',
                a: 'Contact our support team on WhatsApp — we\'ll ask about your existing wiring, the appliances you want to automate, and your smart home ecosystem preference, then recommend the right products. We also offer free consultation calls.',
            },
        ],
    },
});

export const TOPIC_DATA = getTopicData();

/* ─── Single FAQ Item (accordion) ───────────────────────────────────────────── */
const FaqItem = ({ faq, index, color, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div
            style={{
                borderRadius: '0.85rem',
                border: `1.5px solid ${open ? color + '50' : 'rgba(11,49,99,0.1)'}`,
                background: open ? `${color}10` : '#f8faff',
                overflow: 'hidden',
                transition: 'border-color 0.25s, background 0.25s, box-shadow 0.25s',
                boxShadow: open ? `0 4px 20px ${color}20` : 'none',
            }}
        >
            {/* ── Clickable question row ── */}
            <button
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.95rem 1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.85rem',
                    textAlign: 'left',
                }}
            >
                {/* Number badge */}
                <div
                    style={{
                        width: 26, height: 26,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: open ? color : `${color}25`,
                        color: open ? '#fff' : color,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        border: `1.5px solid ${open ? color : color + '40'}`,
                        transition: 'all 0.2s',
                    }}
                >
                    {index + 1}
                </div>

                {/* Question text */}
                <span
                    style={{
                        flex: 1,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        lineHeight: 1.5,
                        color: open ? '#0b3163' : '#334155',
                        transition: 'color 0.2s',
                    }}
                >
                    {faq.q}
                </span>

                {/* Chevron */}
                <div
                    style={{
                        width: 28, height: 28,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: open ? `${color}25` : 'rgba(11,49,99,0.06)',
                        transition: 'all 0.25s',
                    }}
                >
                    <i
                        className="pi pi-chevron-down"
                        style={{
                            fontSize: '0.65rem',
                            color: open ? color : '#94a3b8',
                            display: 'block',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.25s ease, color 0.2s',
                        }}
                    />
                </div>
            </button>

            {/* ── Answer panel (animated) ── */}
            <div
                style={{
                    maxHeight: open ? '400px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.32s ease',
                }}
            >
                <div
                    style={{
                        padding: '0 1.1rem 1.1rem',
                        paddingLeft: '3.7rem',   /* aligns under question text */
                        borderTop: `1px solid ${color}25`,
                        marginTop: 0,
                    }}
                >
                    <p
                        style={{
                            margin: '0.75rem 0 0',
                            fontSize: '0.86rem',
                            lineHeight: 1.8,
                            color: '#475569',
                        }}
                    >
                        {faq.a}
                    </p>
                </div>
            </div>
        </div>
    );
};

/* ─── Modal ──────────────────────────────────────────────────────────────────── */
export const TopicModal = ({ topic, visible, onHide, replacementAllow = 7 }) => {
    if (!topic) return null;
    const data = getTopicData(replacementAllow)[topic];
    if (!data) return null;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            dismissableMask
            style={{ width: '90vw', maxWidth: '780px' }}
            contentStyle={{ padding: 0 }}
            headerStyle={{ display: 'none' }}
            className="support-topic-dialog"
            pt={{
                root: { style: { borderRadius: '1.2rem', overflow: 'hidden', border: 'none' } },
                content: { style: { borderRadius: '1.2rem', padding: 0 } },
                mask: { style: { backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.55)' } },
            }}
        >
            {/* ── Modal Header ──────────────────────────────────────────── */}
            <div
                style={{
                    background: data.gradient,
                    padding: '2rem 2rem 1.5rem',
                    position: 'relative',
                }}
            >
                {/* Close button */}
                <button
                    onClick={onHide}
                    style={{
                        position: 'absolute', top: '1rem', right: '1rem',
                        width: 36, height: 36, borderRadius: '50%',
                        border: '1.5px solid rgba(255,255,255,0.4)',
                        background: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
                >
                    <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                </button>

                <div className="flex align-items-center gap-3">
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width: 54, height: 54,
                            background: 'rgba(255,255,255,0.2)',
                            border: '1.5px solid rgba(255,255,255,0.35)',
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        <i className={data.icon} style={{ fontSize: '1.5rem', color: '#fff' }} />
                    </div>
                    <div>
                        <p className="m-0 font-bold text-white" style={{ fontSize: '1.3rem', lineHeight: 1.2 }}>
                            {data.title}
                        </p>
                        <p className="m-0 mt-1" style={{ color: '#334155', fontSize: '0.85rem' }}>
                            {data.subtitle}
                        </p>
                    </div>
                </div>

                {/* FAQ count badge */}
                <div
                    className="mt-3 flex align-items-center gap-2"
                    style={{
                        display: 'inline-flex',
                        background: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '999px',
                        padding: '0.3rem 0.8rem',
                    }}
                >
                    <i className="pi pi-question-circle" style={{ fontSize: '0.75rem', color: '#0b3163' }} />
                    <span style={{ fontSize: '0.75rem', color: '#0b3163', fontWeight: 600 }}>
                        {data.faqs.length} Frequently Asked Questions
                    </span>
                </div>
            </div>

            {/* ── FAQ List ──────────────────────────────────────────────── */}
            <div
                style={{
                    padding: '1.25rem 1.5rem 1.5rem',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.65rem',
                    background: '#f8faff',
                }}
            >
                {data.faqs.map((faq, i) => (
                    <FaqItem
                        key={i}
                        faq={faq}
                        index={i}
                        color={data.color}
                        defaultOpen={i === 0}
                    />
                ))}
            </div>

            {/* ── Footer actions ────────────────────────────────────────── */}
            <div
                className="flex align-items-center justify-content-between gap-3 flex-wrap px-4 py-3"
                style={{
                    borderTop: '1px solid rgba(11,49,99,0.08)',
                    background: '#ffffff',
                }}
            >
                <p className="m-0 text-sm" style={{ color: '#64748b' }}>
                    <i className="pi pi-info-circle mr-1" />
                    Still didn't find your answer?
                </p>
                <div className="flex gap-2 flex-wrap">
                    <a
                        href={contacts.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex align-items-center gap-2 no-underline border-round-lg px-3 py-2 font-semibold"
                        style={{
                            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                            color: '#fff',
                            fontSize: '0.8rem',
                            boxShadow: '0 2px 10px rgba(34,197,94,0.35)',
                        }}
                    >
                        <i className="pi pi-whatsapp" />
                        WhatsApp Us
                    </a>
                    <a
                        href={`mailto:${contacts.emailSupport}`}
                        className="flex align-items-center gap-2 no-underline border-round-lg px-3 py-2 font-semibold"
                        style={{
                            background: '#f8faff',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: '#334155',
                            fontSize: '0.8rem',
                        }}
                    >
                        <i className="pi pi-envelope" />
                        Email Support
                    </a>
                </div>
            </div>
        </Dialog>
    );
};
